// TASK-042 — /settings/alerts page. Selects the active community via
// the existing entities/community selector + active-community store and
// hands the id to the alerts-panel widget. Header pattern mirrors the
// other pages (Overview, Lifecycle).

import { useEffect } from "react";

import {
  CommunitySelector,
  selectActiveCommunityId,
  useActiveCommunityStore,
  useCommunitiesQuery,
} from "@entities/community";
import { RetryNotice } from "@shared/ui";
import { AlertsPanel } from "@widgets/alerts-panel";

export function AlertsPage() {
  const activeCommunityId = useActiveCommunityStore(selectActiveCommunityId);
  const setActiveCommunityId = useActiveCommunityStore(
    (s) => s.setActiveCommunityId,
  );

  const communitiesQuery = useCommunitiesQuery();

  // Auto-select the first community as soon as the list resolves —
  // matches the other pages.
  useEffect(() => {
    if (activeCommunityId !== null) return;
    const first = communitiesQuery.data?.[0];
    if (first) setActiveCommunityId(first.id);
  }, [activeCommunityId, communitiesQuery.data, setActiveCommunityId]);

  const header = (
    <header className="flex items-center justify-between gap-3 pb-2">
      <h1 className="text-lg font-semibold">Alerts</h1>
      <div className="flex items-center gap-2">
        {communitiesQuery.data && communitiesQuery.data.length > 0 ? (
          <CommunitySelector
            communities={communitiesQuery.data}
            activeCommunityId={activeCommunityId}
            onSelect={setActiveCommunityId}
            label="Active community"
          />
        ) : null}
      </div>
    </header>
  );

  if (communitiesQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <RetryNotice
          title="We couldn't load your communities"
          body={
            communitiesQuery.error?.message ??
            "Check your connection and try again."
          }
          onRetry={() => void communitiesQuery.refetch()}
        />
      </main>
    );
  }

  if (communitiesQuery.data && communitiesQuery.data.length === 0) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <div className="rounded-lg border bg-background p-6 text-center">
          <h2 className="text-base font-semibold">No communities yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Add the bot to a Telegram group to start receiving alerts.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="container flex min-h-screen flex-col gap-6 py-4">
      {header}
      <AlertsPanel communityId={activeCommunityId} />
    </main>
  );
}
